import Header from '@/components/layouts/Header.tsx';
import {
  SiteDeleteConfirmModal,
  SiteDetailModal,
  SiteManagerModal,
  SitePhotoModal,
  SitePhotoUploadModal,
} from '@/components/modals';
import { useMemo, useState } from 'react';
import SiteManagementSearchBar from './components/SiteManagementSearchBar.tsx';
// import SiteManagementStatsCards from './components/SiteManagementStatsCards.tsx';
import SiteAddModal from '@/components/modals/SiteAddModal/index.tsx';
import SiteEditModal from '@/components/modals/SiteEditModal/index.tsx';
import { useDeleteSite } from '@/remote/site-management/delete-site.ts';
import { useSitesQuery } from '@/remote/site-management/fetch-sites.ts';
import {
  useAddSiteManager,
  useRemoveSiteManager,
  useSiteManagersQuery,
} from '@/remote/site-management/site-managers.ts';
import { useMeQuery } from '@/remote/user-management/fetch-me.ts';
import { useUsersQuery } from '@/remote/user-management/fetch-users.ts';
import type { Site } from '@/types/site.ts';
import SiteManagementTable from './components/SiteManagementTable.tsx';

type SortField = keyof Site;
type SortDirection = 'asc' | 'desc';

export default function SiteManagement() {
  const { data: me } = useMeQuery();
  // API 훅들

  const deleteSiteMutation = useDeleteSite();
  const addSiteManagerMutation = useAddSiteManager();
  const removeSiteManagerMutation = useRemoveSiteManager();

  // 상태 관리
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // 모달 상태 (중복 제거: 객체가 있으면 모달 열림, null이면 닫힘)
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedSiteForEdit, setSelectedSiteForEdit] = useState<Site | null>(
    null
  );
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [deleteSiteId, setDeleteSiteId] = useState<string | null>(null);
  const [selectedSiteForDetail, setSelectedSiteForDetail] =
    useState<Site | null>(null);
  const [selectedSiteForManager, setSelectedSiteForManager] =
    useState<Site | null>(null);
  const [selectedSiteForPhoto, setSelectedSiteForPhoto] = useState<Site | null>(
    null
  );

  // 데이터 상태
  const { data: sites } = useSitesQuery();
  const { data: allManagers } = useUsersQuery();

  // 현재 선택된 사이트에 대한 매니저 데이터
  const { data: siteManagers } = useSiteManagersQuery(
    selectedSiteForManager?.siteId || ''
  );

  // 필터링 및 정렬
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      const searchLower = searchTerm.toLowerCase();
      // 하이픈을 제거한 검색어 (숫자만)
      const searchNumbers = searchTerm.replace(/[-\s]/g, '');

      return (
        site.managementNumber?.toLowerCase().includes(searchLower) ||
        site.siteId.toLowerCase().includes(searchLower) ||
        site.managementPosition?.address?.toLowerCase().includes(searchLower) ||
        site.waterSupplyPosition
          ?.flatMap((e) => e.address)
          .join('')
          .toLowerCase()
          .includes(searchLower) ||
        site.name.toLowerCase().includes(searchLower) ||
        // CDMA 번호 검색 (원본 형태)
        site.cdmaNumber?.toLowerCase().includes(searchLower) ||
        // CDMA 번호 검색 (하이픈 제거된 형태)
        (site.cdmaNumber?.replace(/[-\s]/g, '').includes(searchNumbers) &&
          searchNumbers.length > 0)
      );
    });
  }, [sites, searchTerm]);

  const sortedSites = useMemo(() => {
    return [...filteredSites].sort((a, b) => {
      const aValue = a[sortField as keyof Site];
      const bValue = b[sortField as keyof Site];

      // 숫자 비교
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // 특별 처리: siteId나 id 같은 식별자는 자연스러운 정렬 사용
      if (sortField === 'siteId' || sortField === 'id') {
        const aNum = parseInt(String(aValue).replace(/\D/g, ''), 10) || 0;
        const bNum = parseInt(String(bValue).replace(/\D/g, ''), 10) || 0;

        if (aNum !== bNum) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      // 문자열 비교
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [filteredSites, sortField, sortDirection]);

  // 페이지네이션
  const totalPages = Math.ceil(sortedSites.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSites = sortedSites.slice(startIndex, endIndex);

  // 이벤트 핸들러
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (field: string | number | symbol) => {
    const siteField = field as SortField;
    if (sortField === siteField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(siteField);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleDelete = (siteId: string) => {
    setDeleteSiteId(siteId);
  };

  const confirmDelete = async () => {
    if (deleteSiteId) {
      try {
        await deleteSiteMutation.mutateAsync(deleteSiteId);
        setDeleteSiteId(null);
      } catch (error) {
        console.error('사이트 삭제 실패:', error);
        // 에러가 발생해도 모달은 닫지 않아서 사용자가 다시 시도할 수 있도록 함
      }
    }
  };

  const handleEdit = (site: Site) => {
    setSelectedSiteForEdit(site);
  };

  const handleDetail = (site: Site) => {
    setSelectedSiteForDetail(site);
  };

  const handleManagerSetting = (site: Site) => {
    setSelectedSiteForManager(site);
  };

  const addManager = async (managerId: string) => {
    if (selectedSiteForManager) {
      try {
        await addSiteManagerMutation.mutateAsync({
          siteId: selectedSiteForManager.siteId,
          managerId,
        });
      } catch (error) {
        console.error('담당자 추가 실패:', error);
      }
    }
  };

  const removeManager = async (managerId: string) => {
    if (selectedSiteForManager) {
      try {
        await removeSiteManagerMutation.mutateAsync({
          siteId: selectedSiteForManager.siteId,
          managerId,
        });
      } catch (error) {
        console.error('담당자 제거 실패:', error);
      }
    }
  };

  const handleManagePhotos = (site: Site) => {
    setSelectedSiteForPhoto(site);
  };

  // 사진 업로드 성공 핸들러 - 모달 닫기만 처리
  const handlePhotoUploadSuccess = () => {
    setPhotoUploadOpen(false);
  };

  const handleAddSite = () => {
    setAddModalOpen(true);
  };

  const handleAddSiteSuccess = () => {
    setAddModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">현장 관리</h1>
          <p className="text-gray-600">
            물 관리 현장 정보 및 설정을 관리합니다
          </p>
        </div>

        <SiteManagementSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          totalSites={sites.length}
          filteredCount={filteredSites.length}
          onAddSite={handleAddSite}
        />

        <SiteManagementTable
          sites={currentSites}
          searchTerm={searchTerm}
          sortField={sortField}
          sortDirection={sortDirection}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          filteredSites={filteredSites}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onDetail={handleDetail}
          onEdit={handleEdit}
          onManagerSetting={handleManagerSetting}
          onManagePhotos={handleManagePhotos}
          onDelete={handleDelete}
        />

        {/* <SiteManagementStatsCards sites={SiteManagementData} /> */}
      </main>

      {/* 모달들 */}
      {(me?.userLevel === 'SUPER_ADMINISTRATOR' ||
        me?.userLevel === 'HEADQUARTERS_MANAGER') && (
        <SiteAddModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddSiteSuccess}
        />
      )}

      <SiteDeleteConfirmModal
        isOpen={!!deleteSiteId}
        onClose={() => {
          setDeleteSiteId(null);
        }}
        onConfirm={confirmDelete}
      />

      <SiteEditModal
        isOpen={!!selectedSiteForEdit}
        site={selectedSiteForEdit}
        onClose={() => {
          setSelectedSiteForEdit(null);
        }}
      />

      <SitePhotoUploadModal
        isOpen={photoUploadOpen}
        siteId={selectedSiteForPhoto?.siteId || ''}
        onClose={() => setPhotoUploadOpen(false)}
        onUploadSuccess={handlePhotoUploadSuccess}
      />

      <SiteDetailModal
        isOpen={!!selectedSiteForDetail}
        site={selectedSiteForDetail}
        managers={[]}
        onClose={() => {
          setSelectedSiteForDetail(null);
        }}
      />

      <SiteManagerModal
        isOpen={!!selectedSiteForManager}
        site={selectedSiteForManager}
        currentManagers={siteManagers || []}
        allManagers={allManagers || []}
        onClose={() => {
          setSelectedSiteForManager(null);
        }}
        onAddManager={addManager}
        onRemoveManager={removeManager}
      />

      <SitePhotoModal
        isOpen={!!selectedSiteForPhoto}
        site={selectedSiteForPhoto}
        onClose={() => {
          setSelectedSiteForPhoto(null);
        }}
        onUploadPhoto={() => setPhotoUploadOpen(true)}
      />
    </div>
  );
}
